import type { AudioFrame } from "@/utils/arcade/beat";
import { Fx } from "./fx";
import { createProgression, LEVEL_SECONDS } from "./progression";
import { createStars, drawBeatFlash, drawCoverSun, drawStars, drawVinyl, strokeNeon, withAlpha, type Ctx2D } from "./render";
import { createFeedbackQueue, createTally, EMPTY_INPUT, pressedAction, tapInput, type FrameInput, type GameEngine, type GameEnv, type GameProbe, type GameStatus } from "./types";
import { WARN_HURT, WARN_STAND, WARN_STOMP, drawBar, drawBeam, drawBomb, drawBoss, drawBossBar, drawCrawler, drawFire, drawFlyer, drawHanging, drawPickup, drawPillar, drawPit, drawShooter, drawShot, drawWarning, drawWave, type DrawKit } from "./vineDraw";
import { createPlanner } from "./vinePlanner";
import {
  AIRTIME,
  CONTACT_HURT,
  CONTACT_STOMP,
  LEVEL_ONE_SPEED,
  PIT_RESCUE,
  PORTRAIT_WARNING,
  SCROLL_EASE,
  STOMP_BOUNCE,
  STOMPABLE,
  announced,
  arrivalIn,
  contact,
  emptyBox,
  fellIn,
  isStandable,
  makeMetrics,
  makeThing,
  stepRunner,
  supportAt,
  thingBox,
  type Box,
  type Metrics,
  type Pickup,
  type Pit,
  type Runner,
  type Thing,
  type ThingKind,
} from "./vineWorld";

// Vine Runner: a side scrolling platformer on a neon vine. The player is a
// spinning record with one button (jump, and jump again in the air). Each
// level is a run of obstacles and enemies laid down on the beat, then a boss
// that attacks on the beat and goes down to three or more stomps on its core.

const MAX_LIVES = 3;
const INVULNERABLE = 1.5;
// Seconds of running before each level's boss.
const RUN_SECONDS = [20, 18];
const WARN_SECONDS = 0.9;
const BOSS_TIMEOUT = 40;
const BOSS_SCROLL = 0.4;
const FORCE_SPAWN_AFTER = 0.45;
const MAX_THINGS = 60;
// Upright: the latest a turret opens fire, in seconds before it arrives.
const UPRIGHT_SHOT_LEAD = 0.95;

type PatternKind =
  | "bar"
  | "stack"
  | "twin"
  | "hanging"
  | "pit"
  | "pillar"
  | "crawler"
  | "crawlers"
  | "spiker"
  | "hopper"
  | "flyer"
  | "glider"
  | "shooter"
  | "pitCrawler";

// What each level adds, with its weight. A level keeps everything before it.
const UNLOCKS: Array<Array<[PatternKind, number]>> = [
  [
    ["bar", 3],
    ["pit", 2],
    ["crawler", 2.5],
    ["stack", 1],
    ["hanging", 1.2],
  ],
  [
    ["flyer", 2],
    ["shooter", 1.5],
    ["pillar", 1],
    ["twin", 1],
    ["glider", 1],
  ],
  [
    ["spiker", 1.6],
    ["crawlers", 1],
    ["pitCrawler", 1],
  ],
  [["hopper", 1.8]],
];

// The first pattern of a level shows off what the level brings.
const DEBUT: Record<number, PatternKind> = { 2: "flyer", 3: "spiker", 4: "hopper" };

const LEVEL_LABELS = ["Flyers and turrets join in", "Spiked crawlers: jump them, never stomp", "Hoppers bounce on the beat", "Everything, faster"];

type BossMove = "wave" | "bomb" | "low" | "high" | null;

// Four beat bars a boss picks from. Each is safe to play after any other.
const BOSS_BARS: Record<string, BossMove[]> = {
  wave2: ["wave", null, "wave", null],
  wave1: ["wave", null, null, null],
  bombs: ["bomb", "bomb", null, null],
  low: ["low", null, null, null],
  high: ["high", null, null, null],
  double: ["wave", "wave", null, null],
  highWave: ["high", null, "wave", null],
  lowBomb: ["low", "bomb", null, null],
};

interface BossKind {
  name: string;
  hp: number;
  bars: string[];
  perCycle: number;
}

const BOSSES: BossKind[] = [
  { name: "THE SUBWOOFER", hp: 3, bars: ["wave2", "wave1"], perCycle: 1 },
  { name: "STROBE", hp: 3, bars: ["bombs", "low", "wave1"], perCycle: 1 },
  { name: "FEEDBACK", hp: 4, bars: ["wave2", "high", "highWave", "bombs"], perCycle: 2 },
  { name: "THE DROP", hp: 4, bars: ["double", "highWave", "lowBomb", "high", "bombs"], perCycle: 2 },
];

type BossState = "enter" | "idle" | "lower" | "charge" | "return" | "dying" | "leave";

interface Boss {
  type: number;
  name: string;
  hp: number;
  maxHp: number;
  x: number;
  alt: number;
  w: number;
  h: number;
  state: BossState;
  t: number;
  fight: number;
  queue: BossMove[];
  flash: number;
  slam: number;
  body: Thing | null;
  fromX: number;
  fromAlt: number;
  cycles: number;
}

export interface VineRunnerOptions {
  // Tests switch the spawner off and place things by hand.
  autoSpawn?: boolean;
}

export interface VineRunnerLab {
  place(kind: ThingKind, ahead: number, alt?: number): Thing;
  player(): { x: number; alt: number; vUp: number; grounded: boolean; r: number; hj: number };
  things(): Thing[];
  pits(): Pit[];
  metrics(): Metrics;
  scroll(): number;
}

export type VineRunnerEngine = GameEngine & { lab: VineRunnerLab };

export function createVineRunner(env: GameEnv, options: VineRunnerOptions = {}): VineRunnerEngine {
  const { theme, rng, difficulty, art } = env;
  const autoSpawn = options.autoSpawn !== false;
  let { palette } = theme;
  const fx = new Fx(rng, env.reducedMotion, art, env.quiet);
  const progression = createProgression(fx, palette, LEVEL_LABELS);
  let stars = createStars(rng, 80);
  let ridgeSeeds = [rng() * 1000, rng() * 1000];
  let m = makeMetrics(env.width, env.height);

  const tally = createTally();
  const feedbackQueue = createFeedbackQueue();
  const planner = createPlanner();
  const runner: Runner = { alt: m.r, vUp: 0, jumps: 0, grounded: true, air: 0 };
  const box = emptyBox();
  const tops = new Float64Array(3);
  const boxes: Box[] = [];
  const kinds: ThingKind[] = [];

  let things: Thing[] = [];
  let pits: Pit[] = [];
  let pickups: Pickup[] = [];
  let boss: Boss | null = null;

  let time = 0;
  let jumpBuffer = 0;
  let score = 0;
  let lives = MAX_LIVES;
  let combo = 0;
  let over = false;
  let damage = 0;
  let invulnerable = 0;
  let shield = false;
  let bonusJump = false;
  let jumpRescueUsed = false;
  let level = 1;
  let levelRun = 0;
  let phase: "run" | "warn" | "boss" = "run";
  let pendingLevelAt = -1;
  let scroll = 0;
  let distance = 0;
  let spin = 0;
  let beats = 0;
  let beatPeriod = 60 / Math.max(60, theme.bpm);
  let nextSpawnAt = 0;
  let lastLeaveAt = 0;
  let lastPattern: PatternKind | null = null;
  let patternsThisLevel = 0;
  let sinceShield = 0;
  let sinceHeart = 0;
  let zone = 1;
  let botLastTap = -1;
  let stompHinted = false;
  let lastWaveAt = -10;
  let lit = 0;
  const trail: Array<{ x: number; y: number }> = [];

  const speedFactor = () => (LEVEL_ONE_SPEED + 0.055 * Math.min(level - 1, 6)) * (1 + 0.1 * Math.min(1, levelRun / 18));
  const runSpeed = () => m.unit * speedFactor() * difficulty;
  const gapFor = () => Math.max(0.5, 0.75 - 0.04 * (level - 1));
  const spawnChance = () => Math.min(0.92, 0.62 + 0.08 * (level - 1));
  const scrollTarget = () => runSpeed() * (phase === "boss" ? BOSS_SCROLL : 1);
  const runSeconds = () => RUN_SECONDS[Math.min(level - 1, RUN_SECONDS.length - 1)];
  const jumpDistance = () => Math.max(scroll, runSpeed() * 0.8) * AIRTIME;
  const multiplier = () => Math.min(4, 1 + Math.floor(combo / 8)) * progression.multiplier;

  scroll = runSpeed();

  const add = (thing: Thing) => {
    if (things.length >= MAX_THINGS) return thing;
    things.push(thing);
    return thing;
  };

  // ---------------------------------------------------------------- spawning

  const pick = (): PatternKind => {
    const debut = DEBUT[level];
    if (debut && patternsThisLevel === 0) return debut;
    const pool: Array<[PatternKind, number]> = [];
    for (let i = 0; i < Math.min(level, UNLOCKS.length); i++) pool.push(...UNLOCKS[i]);
    let total = 0;
    for (const [kind, weight] of pool) {
      if (kind === "stack" && level === 1 && time < 5) continue;
      if (kind === "hanging" && level === 1 && time < 8) continue;
      total += weight;
    }
    let roll = rng() * total;
    let choice: PatternKind = "bar";
    for (const [kind, weight] of pool) {
      if (kind === "stack" && level === 1 && time < 5) continue;
      if (kind === "hanging" && level === 1 && time < 8) continue;
      roll -= weight;
      if (roll <= 0) {
        choice = kind;
        break;
      }
    }
    // Turret shots arrive early; never send them under a bar you must stay low for.
    if (choice === "shooter" && (lastPattern === "hanging" || lastPattern === "glider")) choice = "bar";
    if (choice === lastPattern && (choice === "hanging" || choice === "stack")) choice = "crawler";
    return choice;
  };

  const note = (x: number, alt: number) => {
    pickups.push({ kind: "note", x, alt, taken: false, phase: rng() * 6 });
  };

  const emit = (kind: PatternKind, x0: number) => {
    const r = m.r;
    const hj = m.hj;
    const D = jumpDistance();
    switch (kind) {
      case "bar": {
        const w = r * (1.3 + rng() * 0.9);
        const h = hj * (0.3 + rng() * (level >= 2 ? 0.5 : 0.4));
        add(makeThing("bar", x0, w, 0, h));
        tally.add("bar");
        if (rng() < 0.5) note(x0 + w / 2, h + hj * 0.55);
        break;
      }
      case "twin": {
        const w = r * 1.4;
        const gap = D * (0.5 + rng() * 0.15);
        add(makeThing("bar", x0, w, 0, hj * (0.3 + rng() * 0.2)));
        add(makeThing("bar", x0 + w + gap, w, 0, hj * (0.35 + rng() * 0.25)));
        tally.add("bar", 2);
        break;
      }
      case "stack": {
        // A double stack: a low step and a tall block behind it. Hop onto the
        // step and jump again, or clear the lot with a double jump.
        const step = r * 1.6;
        const w = r * (1.7 + rng() * 0.5);
        const h = hj * (1.12 + rng() * 0.18);
        add(makeThing("bar", x0, step, 0, hj * (0.4 + rng() * 0.1)));
        add(makeThing("stack", x0 + step, w, 0, h));
        tally.add("stack");
        note(x0 + step + w / 2, h + r * 2.2);
        break;
      }
      case "hanging": {
        const w = r * (2.2 + rng() * 1.8);
        add(makeThing("hanging", x0, w, r * 2.4, m.height * 2));
        tally.add("hanging");
        note(x0 + w / 2, r * 1.1);
        break;
      }
      case "pit": {
        const w = Math.max(r * 2.8, Math.min(D * 0.62, D * (0.3 + rng() * 0.2 + Math.min(level - 1, 4) * 0.02)));
        pits.push({ x: x0, w, passed: false });
        tally.add("pit");
        if (rng() < 0.6) note(x0 + w / 2, hj * 0.85);
        break;
      }
      case "pillar": {
        const pillar = makeThing("pillar", x0, r * 1.6, 0, hj * 1.2);
        pillar.amp = hj * 0.95;
        pillar.omega = Math.PI / beatPeriod;
        pillar.phase = 0;
        add(pillar);
        tally.add("pillar");
        break;
      }
      case "crawler":
      case "crawlers":
      case "spiker": {
        const count = kind === "crawlers" ? 2 : 1;
        const vx = scroll * (0.12 + rng() * 0.14);
        for (let i = 0; i < count; i++) {
          const crawler = makeThing(kind === "spiker" ? "spiker" : "crawler", x0 + i * r * 3.6, r * 2.2, 0, kind === "spiker" ? r * 1.8 : r * 1.35);
          crawler.vx = vx;
          crawler.phase = rng() * 6;
          add(crawler);
          tally.add(kind === "spiker" ? "spiker" : "crawler");
        }
        break;
      }
      case "pitCrawler": {
        const w = Math.max(r * 2.8, D * (0.32 + rng() * 0.12));
        pits.push({ x: x0, w, passed: false });
        tally.add("pit");
        const crawler = makeThing("crawler", x0 + w + D * 0.9, r * 2.2, 0, r * 1.35);
        crawler.vx = scroll * 0.1;
        add(crawler);
        tally.add("crawler");
        break;
      }
      case "hopper": {
        const hopper = makeThing("hopper", x0, r * 2, 0, r * 1.3);
        hopper.vx = scroll * 0.12;
        hopper.amp = hj * 0.55;
        hopper.omega = Math.PI / beatPeriod;
        hopper.phase = 0;
        add(hopper);
        tally.add("hopper");
        break;
      }
      case "flyer": {
        const flyer = makeThing("flyer", x0 + r, 0, r * (2.2 + rng() * 0.6), 0);
        flyer.size = r * 0.95;
        flyer.amp = r * 1.3;
        flyer.omega = (Math.PI * 2) / (beatPeriod * 4);
        flyer.phase = rng() * 6;
        flyer.vx = scroll * 0.25;
        add(flyer);
        tally.add("flyer");
        break;
      }
      case "glider": {
        const glider = makeThing("glider", x0 + r, 0, r * 4.4, 0);
        glider.size = r;
        glider.amp = r * 0.5;
        glider.omega = (Math.PI * 2) / (beatPeriod * 4);
        glider.phase = rng() * 6;
        glider.vx = scroll * 0.2;
        add(glider);
        tally.add("flyer");
        tally.add("glider");
        break;
      }
      case "shooter": {
        const shooter = makeThing("shooter", x0, r * 2.1, 0, r * 1.7);
        shooter.reload = 1;
        add(shooter);
        tally.add("shooter");
        break;
      }
    }
  };

  // Seconds until the leading edge of a thing reaches the record, and until its
  // trailing edge has passed it.
  const timing = (thing: Thing) => {
    thingBox(thing, 0, 0, box);
    const left = box.round ? box.cx - box.rad : box.x0;
    const right = box.round ? box.cx + box.rad : box.x1;
    const speed = scroll + thing.vx;
    return { arrive: (left - (m.px + m.r)) / speed, leave: (right - (m.px - m.r)) / speed };
  };

  const spawnPattern = () => {
    const kind = pick();
    // Upright, patterns start far enough off the edge for the warning strip
    // to show them for the full warning, walkers included.
    const x0 = m.portrait ? Math.max(m.width + m.r * 2, m.px + m.r + scroll * PORTRAIT_WARNING * 1.35) : m.width + m.r * 2;
    const beforeThings = things.length;
    const beforePits = pits.length;
    const beforePickups = pickups.length;
    emit(kind, x0);
    // Keep a clear gap after the last pattern, measured in time at the record.
    const measure = () => {
      let arrive = Infinity;
      let leave = -Infinity;
      for (let i = beforeThings; i < things.length; i++) {
        const t = timing(things[i]);
        arrive = Math.min(arrive, things[i].kind === "shooter" ? t.arrive * 0.55 : t.arrive);
        leave = Math.max(leave, t.leave);
      }
      for (let i = beforePits; i < pits.length; i++) {
        arrive = Math.min(arrive, (pits[i].x - m.px) / scroll);
        leave = Math.max(leave, (pits[i].x + pits[i].w - m.px) / scroll);
      }
      return { arrive, leave };
    };
    let { arrive, leave } = measure();
    const late = lastLeaveAt + gapFor() - (time + arrive);
    if (late > 0 && Number.isFinite(arrive)) {
      for (let i = beforeThings; i < things.length; i++) things[i].x += late * (scroll + things[i].vx);
      for (let i = beforePits; i < pits.length; i++) pits[i].x += late * scroll;
      for (let i = beforePickups; i < pickups.length; i++) pickups[i].x += late * scroll;
      ({ arrive, leave } = measure());
    }
    if (Number.isFinite(leave)) lastLeaveAt = time + leave;
    lastPattern = kind;
    patternsThisLevel += 1;
    // The next pattern may spawn once its arrival would clear the gap.
    const travel = (x0 - m.px) / scroll;
    nextSpawnAt = lastLeaveAt + gapFor() - travel;

    // Shields and hearts roll in at head height in the gap after this pattern.
    const gapX = m.px + (lastLeaveAt - time + gapFor() / 2) * scroll;
    if (!shield && sinceShield > 24) {
      pickups.push({ kind: "shield", x: gapX, alt: m.r * 1.1, taken: false, phase: 0 });
      sinceShield = 0;
    } else if (lives < MAX_LIVES && sinceHeart > 36) {
      pickups.push({ kind: "heart", x: gapX, alt: m.r * 1.1, taken: false, phase: 0 });
      sinceHeart = 0;
    }
  };

  // ---------------------------------------------------------------- boss

  const perchX = () => m.width * (m.portrait ? 0.72 : 0.76);
  const perchAlt = () => m.hj * 1.05;

  const refillQueue = (current: Boss) => {
    const kind = BOSSES[current.type];
    const bars = kind.perCycle + (level > 4 ? 1 : 0);
    current.queue = [];
    for (let i = 0; i < bars; i++) {
      const bar = BOSS_BARS[kind.bars[Math.floor(rng() * kind.bars.length)]];
      current.queue.push(...bar);
    }
  };

  const startBoss = () => {
    const type = (level - 1) % BOSSES.length;
    const kind = BOSSES[type];
    const hp = kind.hp + Math.floor((level - 1) / BOSSES.length);
    boss = {
      type,
      name: kind.name,
      hp,
      maxHp: hp,
      x: m.width + m.r * 6,
      alt: perchAlt(),
      w: m.r * 5.2,
      h: m.r * 5,
      state: "enter",
      t: 0,
      fight: 0,
      queue: [],
      flash: 0,
      slam: 0,
      body: null,
      fromX: m.width + m.r * 6,
      fromAlt: perchAlt(),
      cycles: 0,
    };
    phase = "boss";
    tally.add("boss");
  };

  const bossMove = (current: Boss, move: BossMove) => {
    const r = m.r;
    const front = current.x - current.w / 2;
    if (move === "wave") {
      // Two waves close together are one long jump; a second wave just under
      // an airtime behind the first would leave no time to land, so it is
      // skipped (that only happens at fast tempos).
      const since = time - lastWaveAt;
      if (since > 0.6 && since < AIRTIME + 0.35) return;
      lastWaveAt = time;
      const wave = makeThing("wave", front - r * 1.1, r * 1.1, 0, r * 1.5);
      wave.anchored = true;
      wave.vx = Math.max(scroll * 1.2, (front - m.px) / (2 * beatPeriod));
      add(wave);
      current.slam = 1;
      fx.burst(front, m.ground, palette.danger, 10, 200);
      tally.add("shockwave");
    } else if (move === "bomb") {
      const landX = Math.min(front - r * 2, m.px + r * (5 + rng() * 4));
      const fall = 0.9;
      const bomb = makeThing("bomb", landX + scroll * fall, r * 1.8, m.ground + r, r * 1.3);
      bomb.size = r * 0.75;
      bomb.vAlt = (bomb.alt - bomb.size) / fall;
      bomb.off = 1.2;
      add(bomb);
      tally.add("bomb");
    } else if (move === "low" || move === "high") {
      const beam = makeThing("beam", 0, front, move === "low" ? r * 0.3 : r * 2.35, move === "low" ? r * 0.95 : r * 1.0);
      beam.anchored = true;
      beam.on = beatPeriod * 2;
      beam.off = beam.on + 0.4;
      add(beam);
      tally.add("sweep");
    }
  };

  const bossDive = (current: Boss) => {
    const lowerTime = 0.45;
    const body = makeThing("boss", current.x - current.w / 2, current.w, current.alt, current.h);
    body.anchored = true;
    body.vAlt = current.alt / lowerTime;
    body.delay = lowerTime;
    body.vx = (current.x - m.px) / 1.25;
    add(body);
    current.body = body;
    current.state = "lower";
    current.t = 0;
    if (!stompHinted) {
      stompHinted = true;
      fx.banner("STOMP THE CORE", "Land on it when it comes down", palette.accent);
    }
  };

  const bossReturn = (current: Boss) => {
    if (current.body) {
      current.body.spent = true;
      current.body = null;
    }
    current.state = "return";
    current.t = 0;
    current.fromX = current.x;
    current.fromAlt = current.alt;
  };

  const bossHit = (current: Boss) => {
    current.hp -= 1;
    current.flash = 0.3;
    score += 300 * multiplier();
    fx.burst(current.x, m.ground - current.alt - current.h, palette.accent, 30, 320);
    fx.ring(current.x, m.ground - current.alt - current.h, palette.accent, m.r * 5);
    fx.text(current.x, m.ground - current.alt - current.h - m.r, `+${300 * multiplier()}`, palette.accent, 22);
    fx.kick(8);
    feedbackQueue.push("score");
    tally.add("boss-hit");
    if (current.hp <= 0) {
      if (current.body) current.body.spent = true;
      current.body = null;
      current.state = "dying";
      current.t = 0;
      const bonus = 1500 * level;
      score += bonus;
      tally.add("boss-defeated");
      feedbackQueue.push("win");
      fx.banner("BOSS DOWN", lives < MAX_LIVES ? `+${bonus}  and an extra life` : `+${bonus}`, palette.accent);
      if (lives < MAX_LIVES) lives += 1;
      else shield = true;
      endFight();
    } else {
      bossReturn(current);
    }
  };

  const endFight = () => {
    // Clear the boss's leftovers and get the vine moving again.
    for (const thing of things) {
      if (thing.kind === "beam" || thing.kind === "wave" || thing.kind === "bomb" || thing.kind === "fire") thing.spent = true;
    }
    phase = "run";
    pendingLevelAt = time + 1.6;
    lastLeaveAt = time;
    nextSpawnAt = time + 0.25;
  };

  const updateBoss = (dt: number, audio: AudioFrame) => {
    const current = boss;
    if (!current) return;
    current.t += dt;
    current.flash = Math.max(0, current.flash - dt);
    current.slam = Math.max(0, current.slam - dt * 5);
    if (current.state !== "dying" && current.state !== "leave") current.fight += dt;
    const ease = (t: number) => t * t * (3 - 2 * t);
    switch (current.state) {
      case "enter": {
        const t = Math.min(1, current.t / 1.6);
        current.x = current.fromX + (perchX() - current.fromX) * ease(t);
        current.alt = perchAlt() + Math.sin(time * 2) * m.r * 0.3;
        if (t >= 1) {
          current.state = "idle";
          current.t = 0;
          refillQueue(current);
        }
        break;
      }
      case "idle": {
        current.x += (perchX() - current.x) * Math.min(1, dt * 4);
        current.alt = perchAlt() + Math.sin(time * 2) * m.r * 0.3;
        if (current.fight > BOSS_TIMEOUT) {
          current.state = "leave";
          current.t = 0;
          fx.banner("IT GOT AWAY", "No bonus this time", palette.danger);
          endFight();
          break;
        }
        if (audio.beat) {
          const move = current.queue.shift();
          if (move === undefined) bossDive(current);
          else bossMove(current, move);
        }
        break;
      }
      case "lower":
      case "charge": {
        const body = current.body;
        if (!body) {
          bossReturn(current);
          break;
        }
        current.x = body.x + body.w / 2;
        current.alt = body.alt;
        if (body.delay <= 0) current.state = "charge";
        if (body.x + body.w < m.px - m.r * 2.5) bossReturn(current);
        break;
      }
      case "return": {
        const t = Math.min(1, current.t / 0.9);
        current.x = current.fromX + (perchX() - current.fromX) * ease(t);
        current.alt = current.fromAlt + (perchAlt() - current.fromAlt) * ease(t) + Math.sin(t * Math.PI) * m.hj * 0.8;
        if (t >= 1) {
          current.state = "idle";
          current.cycles += 1;
          refillQueue(current);
        }
        break;
      }
      case "dying": {
        if (Math.floor(current.t * 7) !== Math.floor((current.t - dt) * 7)) {
          const bx = current.x + (rng() - 0.5) * current.w;
          const by = m.ground - current.alt - rng() * current.h;
          fx.burst(bx, by, rng() < 0.5 ? palette.danger : palette.accent, 18, 280);
          fx.ring(bx, by, palette.danger, m.r * 3);
          fx.kick(4);
        }
        current.alt += dt * m.r * 2;
        if (current.t > 1.4) {
          fx.burst(current.x, m.ground - current.alt - current.h / 2, palette.text, 40, 420);
          boss = null;
        }
        break;
      }
      case "leave": {
        current.x += dt * m.width * 0.8;
        current.alt += dt * m.hj;
        if (current.x > m.width + current.w * 2) boss = null;
        break;
      }
    }
  };

  // ---------------------------------------------------------------- player

  const hurt = () => {
    if (invulnerable > 0) return false;
    if (shield) {
      shield = false;
      invulnerable = 1;
      fx.ring(m.px, m.ground - runner.alt, palette.accent, m.r * 4);
      fx.text(m.px, m.ground - runner.alt - m.r * 2, "SHIELD", palette.accent);
      feedbackQueue.push("hurt");
      return true;
    }
    lives -= 1;
    damage += 1;
    combo = 0;
    invulnerable = INVULNERABLE;
    feedbackQueue.push(lives <= 0 ? "crash" : "hurt");
    fx.kick(12);
    fx.burst(m.px, m.ground - runner.alt, palette.danger, 26, 260);
    fx.ring(m.px, m.ground - runner.alt, palette.danger, m.r * 3.5);
    if (lives <= 0) over = true;
    return true;
  };

  const defeat = (thing: Thing) => {
    thing.spent = true;
    thing.dying = 0.3;
    thing.squash = 0;
    const y = m.ground - runner.alt + m.r;
    combo += 1;
    const gain = 100 * multiplier();
    score += gain;
    fx.burst(m.px, y, palette.accent, 18, 220);
    fx.ring(m.px, y, palette.accent, m.r * 3);
    fx.text(m.px, y - m.r * 2, `STOMP +${gain}`, palette.accent);
    feedbackQueue.push("score");
    tally.add("stomp");
  };

  // The upright warning strip: everything still off the right edge but due
  // within the warning time gets a mark at its height, brighter as it nears.
  const drawWarnings = (kit: DrawKit) => {
    const mark = (left: number, speed: number, a0: number, a1: number, tone: number) => {
      if (!announced(m, left, speed)) return;
      const due = arrivalIn(m, left, speed);
      const atEdge = arrivalIn(m, m.width, speed);
      drawWarning(kit, a0, a1, tone, Math.min(1, Math.max(0, (PORTRAIT_WARNING - due) / Math.max(0.05, PORTRAIT_WARNING - atEdge))));
    };
    for (const thing of things) {
      if (thing.anchored || thing.spent) continue;
      thingBox(thing, 0, 0, box);
      const tone = STOMPABLE[thing.kind] ? WARN_STOMP : isStandable(thing.kind) ? WARN_STAND : WARN_HURT;
      if (box.round) mark(box.cx - box.rad, scroll + thing.vx, box.ca - box.rad, box.ca + box.rad, tone);
      else mark(box.x0, scroll + thing.vx, box.a0, Math.min(box.a1, box.a0 + m.r * 3), tone);
    }
    for (const pit of pits) mark(pit.x, scroll, 0, 0, WARN_HURT);
  };

  // Boxes for this frame, reused from a pool.
  const collectBoxes = () => {
    while (boxes.length < things.length) boxes.push(emptyBox());
    kinds.length = things.length;
    for (let i = 0; i < things.length; i++) {
      thingBox(things[i], 0, 0, boxes[i]);
      kinds[i] = things[i].kind;
    }
  };

  // ---------------------------------------------------------------- engine

  const place = (kind: ThingKind, ahead: number, alt = 0): Thing => {
    const r = m.r;
    const x = m.px + ahead;
    let thing: Thing;
    switch (kind) {
      case "crawler":
      case "spiker":
        thing = makeThing(kind, x, r * 2.2, 0, kind === "spiker" ? r * 1.8 : r * 1.35);
        break;
      case "flyer":
      case "glider":
        thing = makeThing(kind, x, 0, alt || r * 2.4, 0);
        thing.size = r * 0.95;
        break;
      case "shooter":
        thing = makeThing(kind, x, r * 2.1, 0, r * 1.7);
        break;
      default:
        thing = makeThing(kind, x, r * 1.6, alt, m.hj * 0.5);
    }
    return add(thing);
  };

  return {
    upgrade(kind) {
      if (kind === "shield") shield = true;
      if (kind === "heart") lives = Math.min(MAX_LIVES, lives + 1);
      if (kind === "jump") bonusJump = true;
    },
    protect(seconds) { invulnerable = Math.max(invulnerable, Math.min(3, seconds)); },
    bloom: 0.45,

    lab: {
      place,
      player: () => ({ x: m.px, alt: runner.alt, vUp: runner.vUp, grounded: runner.grounded, r: m.r, hj: m.hj }),
      things: () => things,
      pits: () => pits,
      metrics: () => m,
      scroll: () => scroll,
    },

    resize(nextWidth, nextHeight) {
      const next = makeMetrics(nextWidth, nextHeight);
      const sx = nextWidth / Math.max(1, m.width);
      const sa = next.r / m.r;
      for (const thing of things) {
        thing.x *= sx;
        thing.w *= thing.kind === "beam" ? sx : sa;
        thing.alt *= sa;
        thing.h *= sa;
        thing.size *= sa;
        thing.amp *= sa;
        thing.vx *= sx;
        thing.vAlt *= sa;
      }
      for (const pit of pits) {
        pit.x *= sx;
        pit.w *= sx;
      }
      for (const pickup of pickups) {
        pickup.x *= sx;
        pickup.alt *= sa;
      }
      if (boss) {
        boss.x *= sx;
        boss.alt *= sa;
        boss.w *= sa;
        boss.h *= sa;
        boss.fromX *= sx;
        boss.fromAlt *= sa;
      }
      runner.alt *= sa;
      runner.vUp *= sa;
      m = next;
      scroll = runSpeed() * (phase === "boss" ? BOSS_SCROLL : 1);
    },

    autopilot() {
      if (over) return EMPTY_INPUT;
      const action = planner.decide({ m, things, pits, pickups, scroll, scrollTarget: scrollTarget(), runner, invulnerable }, time);
      if (action === "jump" && time - botLastTap >= 0.15) {
        botLastTap = time;
        return tapInput(m.px, m.ground);
      }
      return EMPTY_INPUT;
    },

    update(dt, input: FrameInput, audio) {
      if (over) {
        fx.update(dt);
        return;
      }
      time += dt;
      if (phase !== "boss") levelRun += dt;
      invulnerable = Math.max(0, invulnerable - dt);
      if (audio.beatPeriod > 0) beatPeriod = audio.beatPeriod / 1000;
      lit = Math.max(0, lit - dt * 3);

      // Speed: the vine slows right down while a boss is on.
      const target = scrollTarget();
      scroll = target + (scroll - target) * Math.exp(-SCROLL_EASE * dt);
      const move = scroll * dt;
      distance += move;
      spin += dt * (4 + audio.bass * 6);
      score += dt * 12 * difficulty;

      if (audio.beat) {
        beats += 1;
        lit = 1;
        sinceShield += 1;
        sinceHeart += 1;
        if (autoSpawn && phase === "run" && time >= nextSpawnAt && rng() < spawnChance()) spawnPattern();
        // Turrets fire on every other beat (every beat from level 3) while
        // they are far enough away to dodge.
        if (beats % 2 === 0 || level >= 3) {
          for (const thing of things) {
            if (thing.kind !== "shooter" || thing.spent) continue;
            const lead = (thing.x - m.px) / (scroll * 1.45);
            // Upright, a turret may fire from just off the edge (the strip
            // shows it), in the same window of lead time as it always had.
            if (lead < 0.6 || (m.portrait ? lead > UPRIGHT_SHOT_LEAD : thing.x > m.width - m.r)) continue;
            const blocked = things.some((other) => other.kind === "hanging" && !other.spent && other.x > m.px - m.r * 3 && other.x < thing.x);
            if (blocked) continue;
            const shot = makeThing("shot", thing.x - m.r * 0.6, 0, m.r * 0.95, 0);
            shot.size = m.r * 0.42;
            shot.vx = scroll * 0.45;
            add(shot);
            tally.add("shot");
            thing.reload = 0.35;
            fx.burst(thing.x - m.r * 0.6, m.ground - m.r * 0.95, palette.danger, 6, 120);
          }
        }
      }
      if (autoSpawn && phase === "run" && time >= nextSpawnAt + FORCE_SPAWN_AFTER) spawnPattern();

      // The boss shows up once the level's run is done.
      if (autoSpawn && phase === "run" && !boss && pendingLevelAt < 0 && levelRun >= runSeconds() - WARN_SECONDS) {
        phase = "warn";
        const kind = BOSSES[(level - 1) % BOSSES.length];
        fx.banner("WARNING", `${kind.name} is coming`, palette.danger);
        feedbackQueue.push("boss");
      }
      if (phase === "warn" && levelRun >= runSeconds()) startBoss();
      if (pendingLevelAt >= 0 && time >= pendingLevelAt) {
        pendingLevelAt = -1;
        level += 1;
        levelRun = 0;
        patternsThisLevel = 0;
        feedbackQueue.push("level");
      }

      // Move the world.
      for (const thing of things) {
        thing.age += dt;
        if (!thing.anchored) thing.x -= move;
        if (thing.delay > 0) thing.delay -= dt;
        else thing.x -= thing.vx * dt;
        if (thing.dying > 0) {
          thing.dying = Math.max(0, thing.dying - dt);
          thing.squash = Math.min(1, thing.squash + dt * 6);
        }
        if (thing.reload > 0 && thing.kind === "shooter") thing.reload = Math.max(0, thing.reload - dt);
        if (thing.kind === "bomb") {
          thing.alt -= thing.vAlt * dt;
          if (thing.alt - thing.size <= 0) {
            thing.kind = "fire";
            thing.x -= thing.w / 2;
            thing.alt = 0;
            thing.age = 0;
            fx.burst(thing.x + thing.w / 2, m.ground, palette.danger, 14, 200);
            fx.kick(3);
          }
        } else if (thing.kind === "boss" && thing.vAlt > 0) {
          thing.alt = Math.max(0, thing.alt - thing.vAlt * dt);
          if (thing.alt <= 0) {
            thing.vAlt = 0;
            fx.burst(thing.x + thing.w / 2, m.ground, palette.danger, 16, 240);
            fx.kick(6);
          }
        }
      }
      for (const pit of pits) pit.x -= move;
      for (const pickup of pickups) pickup.x -= move;

      // The record.
      collectBoxes();
      const support = supportAt(m.px, m.r, boxes, kinds, things.length, pits, 0, tops);
      const wasGrounded = runner.grounded;
      const prevAlt = runner.alt;
      // A tap just before landing should become a jump, not disappear.
      jumpBuffer = pressedAction(input) ? 0.12 : Math.max(0, jumpBuffer - dt);
      if (runner.grounded) jumpRescueUsed = false;
      if (bonusJump && !jumpRescueUsed && jumpBuffer > 0 && !runner.grounded && runner.jumps >= 2) {
        runner.jumps = 1;
        jumpRescueUsed = true;
      }
      const jumped = stepRunner(runner, dt, m, jumpBuffer > 0, tops, support.topCount, support.groundOk, m.r * 0.45);
      if (jumped) jumpBuffer = 0;
      if (jumped) fx.burst(m.px, m.ground - runner.alt + m.r, palette.secondary, 8, 110);
      if (!wasGrounded && runner.grounded) fx.burst(m.px, m.ground - runner.alt + m.r, withAlpha(palette.text, 0.8), 4, 60);
      if (fellIn(runner, m)) {
        hurt();
        runner.vUp = m.v0 * PIT_RESCUE;
        runner.jumps = 1;
      }

      // Contacts.
      for (let i = 0; i < things.length; i++) {
        const thing = things[i];
        const b = boxes[i];
        const hit = contact(thing.kind, b, m.px, runner.alt, prevAlt, runner.vUp, m.r);
        if (hit === CONTACT_STOMP) {
          runner.vUp = m.v0 * STOMP_BOUNCE;
          runner.jumps = 1;
          runner.grounded = false;
          if (thing.kind === "boss") {
            if (boss) bossHit(boss);
          } else defeat(thing);
        } else if (hit === CONTACT_HURT && invulnerable <= 0) {
          if (hurt() && thing.kind !== "boss" && thing.kind !== "beam" && thing.kind !== "fire") {
            thing.spent = true;
            thing.dying = 0.3;
            fx.burst(m.px + m.r, m.ground - runner.alt, palette.danger, 12, 200);
          }
        } else if (!thing.passed && !thing.spent && b.on) {
          const right = b.round ? b.cx + b.rad : b.x1;
          if (right < m.px - m.r && thing.kind !== "beam" && thing.kind !== "boss") {
            thing.passed = true;
            combo += 1;
            score += 10 * multiplier();
          }
        }
      }
      for (const pit of pits) {
        if (!pit.passed && pit.x + pit.w < m.px - m.r) {
          pit.passed = true;
          combo += 1;
          score += 10 * multiplier();
        }
      }
      for (const pickup of pickups) {
        if (pickup.taken) continue;
        const dx = pickup.x - m.px;
        const da = pickup.alt - runner.alt;
        if (dx * dx + da * da > m.r * m.r * 4) continue;
        pickup.taken = true;
        const y = m.ground - pickup.alt;
        if (pickup.kind === "note") {
          combo += 1;
          const gain = 25 * multiplier();
          score += gain;
          fx.burst(pickup.x, y, palette.accent, 12, 160);
          fx.text(pickup.x, y - m.r, `+${gain}`, palette.accent);
        } else if (pickup.kind === "shield") {
          shield = true;
          fx.banner("SHIELD", "Takes one hit for you", palette.accent);
          fx.burst(pickup.x, y, palette.accent, 20, 200);
        } else {
          lives = Math.min(MAX_LIVES, lives + 1);
          fx.banner("EXTRA LIFE", "", palette.danger);
          fx.burst(pickup.x, y, palette.danger, 20, 200);
        }
      }

      updateBoss(dt, audio);

      things = things.filter((thing) => {
        if (thing.dying > 0) return true;
        if (thing.spent) return false;
        if (thing.kind === "beam") return thing.age <= thing.off;
        if (thing.kind === "fire") return thing.age <= thing.off && thing.x + thing.w > -m.r * 4;
        const right = thing.kind === "flyer" || thing.kind === "glider" || thing.kind === "shot" || thing.kind === "bomb" ? thing.x + thing.size : thing.x + thing.w;
        return right > -m.r * 6;
      });
      pits = pits.filter((pit) => pit.x + pit.w > -m.r * 4);
      pickups = pickups.filter((pickup) => !pickup.taken && pickup.x > -m.r * 4);

      progression.update(dt, (level - 1) * LEVEL_SECONDS, combo);

      if (!env.reducedMotion && !runner.grounded) fx.trail(m.px - m.r, m.ground - runner.alt, palette.primary, -scroll * 0.3, 0);
      trail.unshift({ x: m.px, y: m.ground - runner.alt });
      if (trail.length > 12) trail.pop();
      fx.update(dt);
    },

    draw(ctx, audio) {
      const { width, height, ground, horizon: h0 } = m;
      const uprightSun = Math.min(width * 0.32, (h0 - m.skyTop) * 0.56);
      const sky = ctx.createLinearGradient(0, 0, 0, h0);
      sky.addColorStop(0, palette.backgroundDeep);
      sky.addColorStop(0.6, palette.background);
      sky.addColorStop(1, withAlpha(palette.secondary, 0.4));
      ctx.fillStyle = sky;
      ctx.fillRect(0, 0, width, h0 + 1);
      drawStars(ctx, stars, width, h0 * 0.9, time, palette.text, { x: -distance * 0.00002, y: 0 }, audio.treble);

      ctx.save();
      fx.applyShake(ctx);

      if (m.portrait) {
        // Upright, the sky above the jumps is half the screen, so the scenery
        // is sized to it: a big cover sun, a skyline of equalizer towers that
        // move with the music, and taller hills.
        const room = h0 - m.skyTop;
        const sunRadius = uprightSun * (1 + audio.pulse * 0.03);
        drawCoverSun(ctx, art, palette, width * 0.6, h0 - sunRadius * 0.62, sunRadius, time * 0.35, audio.pulse * 0.6);
        drawSkyline(ctx, h0, room * 0.5, audio);
        drawRidge(ctx, ridgeSeeds[0], h0, room * 0.3, 0.08, withAlpha(palette.backgroundDeep, 0.92), palette.secondary, audio);
        drawRidge(ctx, ridgeSeeds[1], h0, room * 0.16, 0.2, palette.backgroundDeep, palette.primary, audio);
      } else {
        const base = Math.min(width, height);
        const sunRadius = base * 0.18 * (1 + audio.pulse * 0.03);
        drawCoverSun(ctx, art, palette, width * 0.62, h0 - sunRadius * 0.55, sunRadius, time * 0.35, audio.pulse * 0.6);
        drawRidge(ctx, ridgeSeeds[0], h0, height * 0.16, 0.08, withAlpha(palette.backgroundDeep, 0.92), palette.secondary, audio);
        drawRidge(ctx, ridgeSeeds[1], h0, height * 0.09, 0.2, palette.backgroundDeep, palette.primary, audio);
      }

      const floor = ctx.createLinearGradient(0, h0, 0, height);
      floor.addColorStop(0, withAlpha(palette.primary, 0.25));
      floor.addColorStop(0.08, palette.backgroundDeep);
      floor.addColorStop(1, palette.backgroundDeep);
      ctx.fillStyle = floor;
      ctx.fillRect(0, h0, width, height - h0);

      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      const vanishX = width * 0.5;
      const depth = height - h0;
      ctx.lineWidth = 1;
      ctx.strokeStyle = withAlpha(palette.primary, 0.22 + audio.pulse * 0.1);
      ctx.beginPath();
      for (let i = -14; i <= 14; i++) {
        ctx.moveTo(vanishX + i * width * 0.02, h0);
        ctx.lineTo(vanishX + i * width * 0.16, height);
      }
      ctx.stroke();
      const gridPhase = (distance / 90) % 1;
      const rows = m.portrait ? 16 : 12;
      for (let i = 0; i < rows; i++) {
        const t = (i + gridPhase) / rows;
        const y = h0 + depth * t * t;
        ctx.strokeStyle = withAlpha(palette.primary, 0.08 + t * 0.3);
        ctx.lineWidth = 0.8 + t * 1.4;
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(width, y);
        ctx.stroke();
        if (m.portrait && y < ground) {
          // The sun's reflection, broken up by the grid rows.
          const sunX = width * 0.6;
          const reach = uprightSun * 0.8 * (1 - t * 1.4);
          if (reach > 2) {
            ctx.strokeStyle = withAlpha(palette.primary, (0.3 + audio.pulse * 0.1) * (1 - t));
            ctx.lineWidth = 2 + t * 3;
            ctx.beginPath();
            ctx.moveTo(sunX - reach, y);
            ctx.lineTo(sunX + reach, y);
            ctx.stroke();
          }
        }
      }
      ctx.restore();

      const kit: DrawKit = { ctx, art, palette, m, time, pulse: audio.pulse };

      // The vine, broken where the pits are.
      for (const pit of pits) if (pit.x < width && pit.x + pit.w > 0) drawPit(kit, pit.x, pit.w);
      const vine = () => {
        ctx.beginPath();
        let drawing = false;
        for (let x = 0; x <= width + 8; x += 8) {
          let gap = false;
          for (const pit of pits) if (x > pit.x && x < pit.x + pit.w) gap = true;
          const y = ground + 3 + Math.sin((x + distance) * 0.03) * 2.5;
          if (gap) {
            drawing = false;
            continue;
          }
          if (!drawing) ctx.moveTo(x, y);
          else ctx.lineTo(x, y);
          drawing = true;
        }
      };
      strokeNeon(ctx, vine, palette.secondary, 2.2, 0.7);
      ctx.fillStyle = palette.primary;
      for (let x = -(distance % 64); x < width; x += 64) {
        let gap = false;
        for (const pit of pits) if (x > pit.x - 6 && x < pit.x + pit.w + 6) gap = true;
        if (gap) continue;
        const y = ground + 3 + Math.sin((x + distance) * 0.03) * 2.5;
        ctx.beginPath();
        ctx.ellipse(x, y - 6, 8, 3.5, -0.6, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const thing of things) {
        thingBox(thing, 0, 0, box);
        box.on = true;
        const left = box.round ? box.cx - box.rad : box.x0;
        const right = box.round ? box.cx + box.rad : box.x1;
        if (thing.kind !== "beam" && (right < -m.r * 2 || left > width + m.r * 4)) continue;
        switch (thing.kind) {
          case "bar":
          case "stack":
            drawBar(kit, thing, box, lit);
            break;
          case "hanging":
            drawHanging(kit, thing, box);
            break;
          case "pillar":
            drawPillar(kit, box);
            break;
          case "crawler":
          case "spiker":
          case "hopper":
            drawCrawler(kit, thing, box);
            break;
          case "flyer":
          case "glider":
            drawFlyer(kit, thing, box);
            break;
          case "shooter":
            drawShooter(kit, thing, box, thing.reload > 0 ? thing.reload / 0.35 : 0);
            break;
          case "shot":
            drawShot(kit, box);
            break;
          case "wave":
            drawWave(kit, box);
            break;
          case "bomb":
            drawBomb(kit, box, thing.x - scroll * Math.max(0, (thing.alt - thing.size) / Math.max(1, thing.vAlt)));
            break;
          case "fire":
            drawFire(kit, thing, box);
            break;
          case "beam":
            drawBeam(kit, thing, box);
            break;
          case "boss":
            break;
        }
      }

      if (boss) {
        drawBoss(
          kit,
          {
            type: boss.type,
            x: boss.x,
            alt: boss.alt,
            w: boss.w,
            h: boss.h,
            flash: boss.flash,
            exposed: boss.state === "lower" || boss.state === "charge",
            slam: boss.slam,
            dying: boss.state === "dying" ? Math.max(0, 1 - boss.t / 1.4) + 0.2 : 0,
          },
          audio.bass,
        );
      }

      for (const pickup of pickups) if (pickup.x < width + m.r * 2) drawPickup(kit, pickup, drawNote);
      if (m.portrait) drawWarnings(kit);

      const r = m.r;
      const px = m.px;
      const py = ground - runner.alt;
      ctx.save();
      ctx.globalCompositeOperation = "lighter";
      for (let i = 1; i < trail.length; i++) {
        const t = 1 - i / trail.length;
        ctx.strokeStyle = withAlpha(palette.primary, t * 0.3);
        ctx.lineWidth = r * 1.2 * t;
        ctx.beginPath();
        ctx.moveTo(trail[i - 1].x - (i - 1) * 6, trail[i - 1].y);
        ctx.lineTo(trail[i].x - i * 6, trail[i].y);
        ctx.stroke();
      }
      ctx.restore();
      if (shield) {
        ctx.save();
        ctx.strokeStyle = withAlpha(palette.text, 0.6 + Math.sin(time * 8) * 0.2);
        ctx.lineWidth = 2.5;
        ctx.beginPath();
        ctx.arc(px, py, r * 1.55, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
      ctx.globalAlpha = invulnerable > 0 && Math.floor(invulnerable * 12) % 2 === 0 ? 0.35 : 1;
      drawVinyl(ctx, art, palette, px, py, r, spin, 0.3);
      ctx.globalAlpha = 1;

      fx.draw(ctx);
      ctx.restore();

      if (boss) {
        const show = boss.state === "enter" ? Math.min(1, boss.t / 0.6) : boss.state === "dying" ? Math.max(0, 1 - boss.t) : boss.state === "leave" ? 0 : 1;
        drawBossBar(kit, boss.name, Math.max(0, boss.hp), boss.maxHp, show, Boolean(env.quiet));
      }
      fx.drawBanner(ctx, width, height);
      drawBeatFlash(ctx, width, height, progression.fever ? palette.accent : palette.primary, audio.pulse * (progression.fever ? 0.8 : 0.4) + fx.flash);
    },

    setTheme(next, songTitle) {
      // A new song is a new zone of the same run: new colours and skyline,
      // same score, lives and level.
      palette = next.palette;
      zone += 1;
      ridgeSeeds = [rng() * 1000, rng() * 1000];
      stars = createStars(rng, 80);
      fx.banner(`ZONE ${zone}`, songTitle, palette.primary);
      fx.flash = Math.min(1, fx.flash + 0.3);
    },

    feedback() {
      return feedbackQueue.drain();
    },

    probe(): GameProbe {
      let threats = 0;
      for (const thing of things) {
        if (thing.spent || thing.dying > 0 || thing.passed) continue;
        thingBox(thing, 0, 0, box);
        if (thing.kind === "beam") {
          threats += 1;
          continue;
        }
        const left = box.round ? box.cx - box.rad : box.x0;
        const right = box.round ? box.cx + box.rad : box.x1;
        if ((left < m.width && right > m.px - m.r) || announced(m, left, scroll + thing.vx)) threats += 1;
      }
      for (const pit of pits) if (!pit.passed && ((pit.x < m.width && pit.x + pit.w > m.px - m.r) || announced(m, pit.x, scroll))) threats += 1;
      if (boss && boss.state !== "dying" && boss.state !== "leave") threats += 1;
      let onScreen = 0;
      for (const pickup of pickups) if (pickup.x < m.width) onScreen += 1;
      const intensity = (speedFactor() / LEVEL_ONE_SPEED) * (0.75 / gapFor()) * (0.8 + 0.2 * spawnChance() / 0.62);
      return {
        threats,
        pickups: onScreen,
        spawned: tally.spawned,
        damage,
        intensity,
        boss: Boolean(boss && boss.state !== "dying" && boss.state !== "leave"),
        mission: null,
      };
    },

    status(): GameStatus {
      return { score: Math.floor(score), lives, maxLives: MAX_LIVES, combo, meter: null, over, level, fever: progression.fever };
    },
  };

  function drawRidge(ctx: Ctx2D, seed: number, base: number, amplitude: number, parallax: number, fill: string, line: string, audio: AudioFrame) {
    const offset = distance * parallax;
    const trace = () => {
      ctx.beginPath();
      for (let x = -20; x <= m.width + 20; x += 18) {
        const world = (x + offset) * 0.004 + seed;
        const peak = Math.abs(Math.sin(world) * 0.6 + Math.sin(world * 2.7) * 0.3 + Math.sin(world * 6.1) * 0.1);
        const y = base - peak * amplitude * (1 + audio.mid * 0.3);
        if (x === -20) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
      }
    };
    trace();
    ctx.lineTo(m.width + 20, base);
    ctx.lineTo(-20, base);
    ctx.closePath();
    ctx.fillStyle = fill;
    ctx.fill();
    strokeNeon(ctx, trace, line, 1.2, 0.6);
  }

  // A far skyline of equalizer towers. Each tower has a fixed height from the
  // zone's seed plus a little of its band of the spectrum, and a lit cap.
  function drawSkyline(ctx: Ctx2D, base: number, tallest: number, audio: AudioFrame) {
    const step = Math.max(12, m.width / 20);
    const offset = distance * 0.04;
    const first = Math.floor(offset / step);
    const bands = audio.spectrum.length;
    ctx.save();
    ctx.fillStyle = withAlpha(palette.backgroundDeep, 0.88);
    for (let i = first - 1; x(i) < m.width; i++) ctx.fillRect(x(i), base - height(i), step - 3, height(i));
    ctx.globalCompositeOperation = "lighter";
    ctx.fillStyle = withAlpha(palette.secondary, 0.45);
    for (let i = first - 1; x(i) < m.width; i++) ctx.fillRect(x(i), base - height(i), step - 3, 2);
    ctx.restore();

    function x(i: number) {
      return i * step - offset;
    }
    function height(i: number) {
      const seed = ridgeSeeds[0] * 0.37 + i * 12.9898;
      const noise = Math.sin(seed) * 43758.5453;
      const rest = 0.25 + 0.55 * (noise - Math.floor(noise));
      const band = bands ? audio.spectrum[((i % bands) + bands) % bands] ?? 0 : 0;
      return tallest * Math.min(1, rest + band * 0.2);
    }
  }
}

export function drawNote(ctx: Ctx2D, x: number, y: number, size: number, color: string) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1.5, size * 0.14);
  ctx.beginPath();
  ctx.ellipse(x - size * 0.2, y + size * 0.3, size * 0.32, size * 0.24, -0.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.beginPath();
  ctx.moveTo(x + size * 0.1, y + size * 0.28);
  ctx.lineTo(x + size * 0.1, y - size * 0.5);
  ctx.quadraticCurveTo(x + size * 0.5, y - size * 0.3, x + size * 0.42, y - size * 0.02);
  ctx.stroke();
  ctx.restore();
}
